import React from "react";
import { Box, Button, Chip, Paper, Skeleton, Stack, Typography } from "@mui/material";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import PlayArrowOutlinedIcon from "@mui/icons-material/PlayArrowOutlined";
import { useThemeColors } from "../../../hooks/useThemeColors";
import { humanize } from "../roles/permissionUtils";
import { ApiApplicationGrant, ExternalResource } from "./types";

interface GrantedSummaryPanelProps {
  resources: ExternalResource[];
  grants: Record<string, ApiApplicationGrant>;
  loading: boolean;
  tokenPrefix?: string | null;
  hosts: string[];
  wildcard: boolean;
  onShowDocs: (resource: ExternalResource | null) => void;
  onTry: (resource: ExternalResource | null) => void;
  onRevoke: (resource: ExternalResource) => void;
}

/**
 * Sticky preview of what the application can reach: granted API tables as
 * chips (click = docs, x = revoke View), other granted models as a count,
 * allowed hosts, and the docs / try shortcuts.
 */
const GrantedSummaryPanel: React.FC<GrantedSummaryPanelProps> = ({
  resources,
  grants,
  loading,
  tokenPrefix,
  hosts,
  wildcard,
  onShowDocs,
  onTry,
  onRevoke,
}) => {
  const colors = useThemeColors();
  const headingColor = colors.primary[100];

  const grantedResources = resources.filter((r) => !!grants[r.key]?.can_view);
  const resourceKeys = new Set(resources.map((r) => r.key));
  const otherGranted = Object.values(grants).filter(
    (g) => (g.can_view || g.can_add || g.can_change || g.can_delete) && !resourceKeys.has(`${g.app_label}.${g.model}`),
  ).length;

  return (
    <Paper
      sx={{
        boxShadow: 0,
        border: "1px solid",
        borderColor: "divider",
        borderRadius: 3,
        p: 2.5,
        position: { md: "sticky" },
        top: { md: 88 },
      }}
    >
      <Typography fontWeight={700} sx={{ color: headingColor }}>
        Access preview
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
        What a holder of this token can read.
      </Typography>

      {loading ? (
        <Stack spacing={1}>
          <Skeleton variant="rounded" height={28} />
          <Skeleton variant="rounded" height={28} width="70%" />
        </Stack>
      ) : (
        <>
          <Typography variant="caption" color="text.secondary">
            API tables · {grantedResources.length} of {resources.length}
          </Typography>
          <Stack direction="row" flexWrap="wrap" gap={0.75} sx={{ mt: 0.5, mb: 1.5 }}>
            {grantedResources.length === 0 && (
              <Typography variant="body2" color="text.secondary">
                Nothing granted yet. Tick a table on the left.
              </Typography>
            )}
            {grantedResources.map((r) => (
              <Chip
                key={r.key}
                label={humanize(r.model)}
                size="small"
                color="primary"
                variant="filled"
                onClick={() => onShowDocs(r)}
                onDelete={() => onRevoke(r)}
              />
            ))}
          </Stack>
          {otherGranted > 0 && (
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1.5 }}>
              Plus {otherGranted} other model{otherGranted === 1 ? "" : "s"} granted under Advanced (no
              external endpoint yet).
            </Typography>
          )}
        </>
      )}

      <Typography variant="caption" color="text.secondary">
        Allowed hosts
      </Typography>
      <Box sx={{ mb: 1.5 }}>
        {wildcard ? (
          <Chip label="* (any origin)" size="small" variant="outlined" />
        ) : hosts.length === 0 ? (
          <Typography variant="body2" color="text.secondary">
            None yet.
          </Typography>
        ) : (
          <Stack direction="row" flexWrap="wrap" gap={0.5} sx={{ mt: 0.5 }}>
            {hosts.map((h) => (
              <Chip key={h} label={h} size="small" variant="outlined" />
            ))}
          </Stack>
        )}
      </Box>

      {tokenPrefix && (
        <>
          <Typography variant="caption" color="text.secondary">
            Token
          </Typography>
          <Typography sx={{ fontFamily: "monospace", fontSize: "0.8125rem", mb: 1.5 }}>
            {tokenPrefix}••••••••
          </Typography>
        </>
      )}

      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
        <Button
          size="small"
          variant="outlined"
          startIcon={<MenuBookOutlinedIcon />}
          onClick={() => onShowDocs(grantedResources[0] ?? null)}
          disabled={loading}
        >
          API docs
        </Button>
        <Button
          size="small"
          variant="contained"
          startIcon={<PlayArrowOutlinedIcon />}
          onClick={() => onTry(grantedResources[0] ?? null)}
          disabled={loading}
        >
          Try a token
        </Button>
      </Stack>
    </Paper>
  );
};

export default GrantedSummaryPanel;
